import { useEffect, useRef, useState } from "react";
import type { ReactNode, ButtonHTMLAttributes, CSSProperties } from "react";
import { Link } from "react-router-dom";
import type { Tone } from "../lib/vehicleStatus";
import { initials } from "../lib/format";
import { closestFuzzyMatch } from "../lib/fuzzyMatch";
import { logoUrl } from "../lib/logo";

/**
 * Every crumb but the last is a link to an ancestor page; the last is
 * always the current page and is never a link, matching how every
 * hand-written breadcrumb in this app already worked before this
 * component existed. Home is the trail's fixed root everywhere it's
 * used — it's also the app's back-navigation floor (AppShell's
 * useHistoryFloor), so "tap Home" and "swipe back enough times" always
 * agree on where the trail ends.
 */
export function Breadcrumbs({ trail }: { trail: { label: string; to?: string }[] }) {
  return (
    <nav className="row-tight breadcrumbs" aria-label="Breadcrumb">
      {trail.map((t, i) => (
        <span key={i} style={{ display: "contents" }}>
          {i > 0 && <span className="muted small">/</span>}
          {t.to ? (
            <Link to={t.to} className="muted small">
              {t.label}
            </Link>
          ) : (
            <span className="small">{t.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

export function Badge({ tone = "neutral", children }: { tone?: Tone; children: ReactNode }) {
  const cls = tone === "neutral" ? "badge" : `badge ${tone}`;
  return (
    <span className={cls}>
      {tone !== "neutral" && <span className="dot" aria-hidden="true" />}
      {children}
    </span>
  );
}

function hueFromString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % 360;
}

/**
 * Colour-codes role names by hashing the name to a hue — the old Glide
 * app gave each role its own badge colour (blue Admin, orange
 * Director/Contractor); this gets the same colourful effect without a
 * hand-maintained per-role colour table, so a role added in the database
 * gets a colour for free instead of falling back to a plain grey badge.
 */
export function RoleBadge({ name, children }: { name: string; children?: ReactNode }) {
  return (
    <span className="badge role" style={{ "--hue": hueFromString(name) } as CSSProperties}>
      {children ?? name}
    </span>
  );
}

/**
 * Initials-on-a-hashed-hue fallback, for anywhere a customer/company logo
 * would go if the app stored one — it doesn't (nothing in the schema or
 * Storage holds a logo image, confirmed by inspection), so this is the
 * only honest "avatar" available today rather than leaving blank image
 * space or firing off a per-row favicon lookup.
 */
export function Avatar({ name, size = 36 }: { name: string; size?: number }) {
  return (
    <span
      aria-hidden="true"
      className="avatar"
      style={
        {
          width: size,
          height: size,
          fontSize: size * 0.38,
          "--hue": hueFromString(name),
        } as CSSProperties
      }
    >
      {initials(name)}
    </span>
  );
}

/**
 * A client's real logo where one can be resolved (via Logo.dev, off the
 * company's website domain or a best-guess from its name — see lib/logo.ts,
 * ported from the same lookup the former Glide app used), falling back to
 * the hashed-hue initials Avatar on a missing domain or a failed image
 * load. Same fallback contract as Avatar so callers can swap one for the
 * other without changing layout.
 */
export function CompanyLogo({
  name,
  website,
  size = 36,
}: {
  name: string;
  website?: string | null;
  size?: number;
}) {
  const [errored, setErrored] = useState(false);
  const url = logoUrl(website, name);
  if (!url || errored) return <Avatar name={name} size={size} />;
  return (
    <img
      src={url}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      onError={() => setErrored(true)}
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        objectFit: "contain",
        background: "#fff",
        border: "1px solid var(--border)",
        flex: "none",
      }}
    />
  );
}

/** A commissioning/progress percentage given the same visual weight an
 * avatar gets — the point of the percentage-first redesign is that this
 * number is read before anything else on the row, not buried in a badge. */
export function ProgressBadge({ percent, size = "md" }: { percent: number; size?: "md" | "sm" }) {
  const tone = percent >= 100 ? "good" : percent > 0 ? "warn" : undefined;
  const cls = ["progress-badge", size === "sm" ? "sm" : "", tone ?? ""].filter(Boolean).join(" ");
  return (
    <span className={cls} aria-hidden="true">
      {Math.round(percent)}%
    </span>
  );
}

/**
 * One compact, entirely-clickable "browse a list, open an entity" row:
 * avatar + title (with an optional top-right corner label, e.g. a
 * reference code) + one meta line + a tags line + chevron. Generalises
 * the row Visits.tsx built first — every list that's really "here are
 * some things, tap one to open it" (Fleet, Companies, People, Tickets,
 * Client Offices) should look like this, not a labelled-field table.
 */
export function EntityRow({
  to,
  avatarName,
  avatar,
  title,
  corner,
  meta,
  tags,
}: {
  to: string;
  avatarName: string;
  /** Overrides the default hashed-hue Avatar, e.g. <CompanyLogo/> — falls
   * back to Avatar itself on a failed image load, so callers don't need
   * their own fallback handling. */
  avatar?: ReactNode;
  title: ReactNode;
  corner?: ReactNode;
  meta?: ReactNode;
  tags?: ReactNode;
}) {
  return (
    <Link to={to} className="entity-row">
      {avatar ?? <Avatar name={avatarName} size={40} />}
      <div className="entity-row-body">
        <div className="entity-row-title">
          <strong>{title}</strong>
          {corner != null && <span className="entity-row-corner muted small">{corner}</span>}
        </div>
        {meta != null && <div className="entity-row-meta muted small">{meta}</div>}
        {tags != null && <div className="row-tight entity-row-tags">{tags}</div>}
      </div>
      <span className="entity-row-chevron" aria-hidden="true">
        ›
      </span>
    </Link>
  );
}

/** Skeleton row matching EntityRow's exact dimensions, so a loading list
 * doesn't shift layout once real rows arrive. */
export function EntityRowSkeleton() {
  return (
    <div className="entity-row entity-row-skeleton" aria-hidden="true">
      <span className="skeleton" style={{ width: 40, height: 40, borderRadius: "50%" }} />
      <div className="entity-row-body">
        <div className="skeleton" style={{ width: "55%", height: 14 }} />
        <div className="skeleton" style={{ width: "75%", height: 12, marginTop: 7 }} />
        <div className="skeleton" style={{ width: "35%", height: 18, marginTop: 7 }} />
      </div>
    </div>
  );
}

/**
 * A `<tr>` with the click/keyboard handling that FleetList.tsx and
 * CompanyList.tsx used to each hand-roll separately — for genuinely
 * tabular, multi-column data that still needs a clickable row without
 * becoming a full EntityRow (e.g. CompanyDetail's Sites tab).
 */
export function ClickableRow({
  onOpen,
  children,
}: {
  onOpen: () => void;
  children: ReactNode;
}) {
  return (
    <tr
      className="clickable"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      {children}
    </tr>
  );
}

export interface TrendPoint {
  label: string;
  value: number;
}

/** Inline-SVG trend line — no charting library in this codebase, and a
 * handful of simple Dashboard charts don't warrant adding one. Labelled
 * axes (min/mid/max on Y, first/middle/last on X) plus a hover/tap
 * readout are the two things a bare polyline was missing compared to
 * Glide's charts. width="100%" + preserveAspectRatio="none" scales to
 * the card responsively; pointer coordinates are mapped back into the
 * fixed internal viewBox space via getBoundingClientRect() rather than
 * relying on the (CSS-scaled) rendered pixel size. */
export function TrendLine({
  data,
  height = 120,
  color = "var(--accent)",
  valueFormat = (v: number) => String(Math.round(v)),
}: {
  data: TrendPoint[];
  height?: number;
  color?: string;
  valueFormat?: (v: number) => string;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hover, setHover] = useState<number | null>(null);
  if (data.length < 2) return null;

  const VB_W = 600;
  const padTop = 14;
  const padBottom = 20;
  const padX = 4;
  const plotH = height - padTop - padBottom;
  const values = data.map((d) => d.value);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const stepX = (VB_W - padX * 2) / (data.length - 1);
  const xAt = (i: number) => padX + i * stepX;
  const yAt = (v: number) => padTop + plotH - ((v - min) / range) * plotH;

  const linePoints = data.map((d, i) => `${xAt(i).toFixed(1)},${yAt(d.value).toFixed(1)}`).join(" ");
  const areaPoints = `${xAt(0).toFixed(1)},${(padTop + plotH).toFixed(1)} ${linePoints} ${xAt(data.length - 1).toFixed(1)},${(padTop + plotH).toFixed(1)}`;
  const gradId = `tl-grad-${Math.abs(color.split("").reduce((h, c) => (h * 31 + c.charCodeAt(0)) | 0, 0))}`;

  function nearestIndex(clientX: number): number | null {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return null;
    const relX = ((clientX - rect.left) / rect.width) * VB_W;
    let nearest = 0;
    let best = Infinity;
    for (let i = 0; i < data.length; i++) {
      const d = Math.abs(xAt(i) - relX);
      if (d < best) {
        best = d;
        nearest = i;
      }
    }
    return nearest;
  }

  const mid = (max + min) / 2;
  const xLabelIdx = [0, Math.floor((data.length - 1) / 2), data.length - 1];

  return (
    <div style={{ position: "relative" }}>
      <svg
        ref={svgRef}
        width="100%"
        height={height}
        viewBox={`0 0 ${VB_W} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="Trend line — tap or hover a point for its value"
        onPointerMove={(e) => setHover(nearestIndex(e.clientX))}
        onPointerDown={(e) => setHover(nearestIndex(e.clientX))}
        onPointerLeave={() => setHover(null)}
        style={{ touchAction: "pan-y", cursor: "crosshair", display: "block", overflow: "visible" }}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.28} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>

        {[max, mid, min].map((v, i) => {
          const y = yAt(v);
          return (
            <g key={i}>
              <line x1={padX} y1={y} x2={VB_W - padX} y2={y} stroke="var(--border)" strokeWidth={1} strokeDasharray={i === 1 ? "3 3" : undefined} />
              <text x={padX} y={y - 4} fontSize={11} fill="var(--ink-3)">
                {valueFormat(v)}
              </text>
            </g>
          );
        })}

        <polygon points={areaPoints} fill={`url(#${gradId})`} stroke="none" />
        <polyline points={linePoints} fill="none" stroke={color} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />

        {xLabelIdx.map((i, k) => (
          <text
            key={k}
            x={xAt(i)}
            y={height - 5}
            fontSize={11}
            fill="var(--ink-3)"
            textAnchor={i === 0 ? "start" : i === data.length - 1 ? "end" : "middle"}
          >
            {data[i]!.label}
          </text>
        ))}

        {hover != null && (
          <>
            <line x1={xAt(hover)} y1={padTop} x2={xAt(hover)} y2={padTop + plotH} stroke={color} strokeWidth={1} strokeDasharray="3 3" opacity={0.6} />
            <circle cx={xAt(hover)} cy={yAt(data[hover]!.value)} r={4.5} fill={color} stroke="var(--surface)" strokeWidth={2} />
          </>
        )}
      </svg>

      {hover != null && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: `${(xAt(hover) / VB_W) * 100}%`,
            transform: `translateX(${hover === 0 ? "0%" : hover === data.length - 1 ? "-100%" : "-50%"})`,
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 6,
            padding: "4px 8px",
            fontSize: 12,
            fontWeight: 600,
            boxShadow: "var(--shadow)",
            pointerEvents: "none",
            whiteSpace: "nowrap",
            zIndex: 1,
          }}
        >
          <div className="muted" style={{ fontWeight: 500, fontSize: 11 }}>{data[hover]!.label}</div>
          <div style={{ color }}>{valueFormat(data[hover]!.value)}</div>
        </div>
      )}
    </div>
  );
}

/** Horizontal bar chart, same "no library needed" reasoning as TrendLine.
 * A light value-axis (0/half/max gridlines with labels) gives it the
 * same "read it like a BI chart" scale reference TrendLine got, and
 * hovering/tapping a row highlights that bar and calls out its exact
 * value in a tooltip — even though the value is always shown at the bar's
 * end too, the highlight makes clear which row you're reading when
 * several bars are close in length. */
export function BarChart({
  data,
  color = "var(--accent)",
  valueFormat = (v: number) => String(v),
}: {
  data: { label: string; value: number }[];
  color?: string;
  valueFormat?: (v: number) => string;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const max = Math.max(...data.map((d) => d.value), 1);
  const ticks = [0, max / 2, max];
  return (
    <div>
      <div className="row-tight" style={{ paddingLeft: 128, marginBottom: 4 }}>
        {ticks.map((t, i) => (
          <span key={i} className="small muted" style={{ flex: i === ticks.length - 1 ? "0 0 auto" : 1, marginLeft: i === 0 ? 0 : undefined }}>
            {valueFormat(t)}
          </span>
        ))}
      </div>
      <div style={{ display: "grid", gap: 6 }}>
        {data.map((d) => {
          const isHover = hover === d.label;
          return (
            <div
              key={d.label}
              className="row-tight"
              style={{ gap: 8, cursor: "pointer" }}
              onPointerEnter={() => setHover(d.label)}
              onPointerDown={() => setHover(d.label)}
              onPointerLeave={() => setHover((h) => (h === d.label ? null : h))}
            >
              <span
                className="small"
                style={{ width: 120, flex: "0 0 auto", textAlign: "right", color: isHover ? "var(--ink)" : "var(--ink-2)", fontWeight: isHover ? 640 : 400 }}
              >
                {d.label}
              </span>
              <div style={{ position: "relative", flex: 1, background: "var(--surface-2)", borderRadius: 4, overflow: "hidden" }}>
                {ticks.slice(1, -1).map((t, i) => (
                  <div key={i} style={{ position: "absolute", left: `${(t / max) * 100}%`, top: 0, bottom: 0, width: 1, background: "var(--border)" }} />
                ))}
                <div
                  style={{
                    width: `${(d.value / max) * 100}%`,
                    background: color,
                    height: 14,
                    borderRadius: 4,
                    opacity: isHover ? 1 : 0.85,
                    boxShadow: isHover ? `0 0 0 1.5px ${color}` : undefined,
                    transition: "opacity 0.1s ease",
                  }}
                />
              </div>
              <span className="small" style={{ width: 34, flex: "0 0 auto", fontWeight: isHover ? 640 : 400 }}>
                {valueFormat(d.value)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Chart-shaped loading placeholder for BarChart — bars of decreasing
 * width read as "a chart is coming" rather than the generic text-row
 * skeleton (Loading) that doesn't hint at what's about to render. */
export function BarChartSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div style={{ display: "grid", gap: 6 }} aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="row-tight" style={{ gap: 8 }}>
          <div className="skeleton" style={{ width: 120, height: 14, flex: "0 0 auto" }} />
          <div className="skeleton" style={{ flex: 1, height: 14, width: `${70 - i * 15}%` }} />
        </div>
      ))}
    </div>
  );
}

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "default" | "primary" | "danger" | "ghost";
  size?: "md" | "sm";
  block?: boolean;
};

export function Button({
  variant = "default",
  size = "md",
  block,
  className = "",
  ...rest
}: BtnProps) {
  const cls = [
    "btn",
    variant !== "default" ? variant : "",
    size === "sm" ? "sm" : "",
    block ? "block" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return <button type="button" className={cls} {...rest} />;
}

export function Card({
  title,
  actions,
  children,
  pad = true,
  className,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  pad?: boolean;
  className?: string;
}) {
  return (
    <section className={className ? `card ${className}` : "card"}>
      {(title || actions) && (
        <header className="card-head">
          {typeof title === "string" ? <h2>{title}</h2> : title}
          {actions}
        </header>
      )}
      <div className={pad ? "card-pad" : undefined}>{children}</div>
    </section>
  );
}

export function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: ReactNode;
  tone?: "bad" | "warn";
}) {
  return (
    <div className={`card stat${tone ? ` is-${tone}` : ""}`}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  );
}

/**
 * Shared label/value definition-list grid — was hand-rolled with slightly
 * different column widths in CompanyDetail/VehicleDetail/PersonDetail;
 * one component here instead. `hideEmpty` drops rows whose value is the
 * "—" empty-sentinel (or null/undefined/"") already used throughout the
 * app for "nothing recorded" — for pages with many rarely-populated
 * fields (legacy IDs, optional dates) this is what actually removes the
 * dead space, not shrinking the fields that do have values.
 */
export function Facts({
  rows,
  hideEmpty = false,
}: {
  rows: [string, ReactNode][];
  hideEmpty?: boolean;
}) {
  const visible = hideEmpty ? rows.filter(([, v]) => v !== "—" && v != null && v !== "") : rows;
  return (
    <dl style={{ margin: 0, display: "grid", gridTemplateColumns: "minmax(130px,auto) 1fr", gap: "7px 14px" }}>
      {visible.map(([k, v]) => (
        <div key={k} style={{ display: "contents" }}>
          <dt className="muted small">{k}</dt>
          <dd style={{ margin: 0, fontSize: 14 }}>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Field({
  label,
  hint,
  children,
  className,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  /** For the odd field that should not be as wide as its neighbours --
   * a pick-list of five words does not need the width of a description. */
  className?: string;
}) {
  return (
    <label className={className ? `field ${className}` : "field"}>
      <span>{label}</span>
      {children}
      {hint && <div className="hint">{hint}</div>}
    </label>
  );
}

export interface ComboboxOption {
  value: string;
  label: string;
}

/**
 * A type-to-filter text input backed by a fixed option list — for a
 * <select> long enough that scrolling every option (all a plain <select>
 * offers on mobile, with no search) is slower than typing a few letters.
 * Reuses the same .suggest-list/.suggest-item styling as the Quote
 * Builder's product-search dropdown. onCreate, when given, offers a
 * trailing "+ Add …" row whenever the typed text doesn't exactly match an
 * existing option, so free-text entry and picking from the list share one
 * field instead of a separate "new X" toggle.
 */
export function Combobox({
  value,
  options,
  onChange,
  onCreate,
  createLabel,
  placeholder,
  ariaLabel,
}: {
  value: string;
  options: ComboboxOption[];
  onChange: (value: string) => void;
  onCreate?: (query: string) => void;
  createLabel?: (query: string) => string;
  placeholder?: string;
  ariaLabel?: string;
}) {
  // null = showing the current selection; a string (including "") = the
  // field is being edited and this is what's been typed so far.
  const [query, setQuery] = useState<string | null>(null);
  const [active, setActive] = useState(-1);
  const boxRef = useRef<HTMLDivElement>(null);

  const editing = query !== null;
  const selectedLabel = options.find((o) => o.value === value)?.label ?? "";
  const needle = editing ? query.trim().toLowerCase() : "";
  const filtered = needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;
  const exactMatch = needle !== "" && options.some((o) => o.label.toLowerCase() === needle);
  const showCreate = Boolean(onCreate && editing && query.trim() && !exactMatch);
  const totalRows = filtered.length + (showCreate ? 1 : 0);

  function pick(o: ComboboxOption) {
    onChange(o.value);
    setQuery(null);
    setActive(-1);
  }

  function create() {
    const name = query?.trim();
    if (!onCreate || !name) return;
    onCreate(name);
    setQuery(null);
    setActive(-1);
  }

  // Leaving the field (clicking elsewhere, tabbing away) used to just
  // discard whatever had been typed and revert to the prior selection --
  // so typing an existing option's name exactly (e.g. "lg" for "LG"), or
  // with a minor typo (e.g. "extorn" for "Extron"), and clicking straight
  // into the next field silently lost it instead of resolving to it, with
  // no error and no indication anything went wrong. Explicitly picking a
  // suggestion or pressing "+ Add" still works exactly as before (those
  // fire on mousedown, before this ever runs) -- a name that's genuinely
  // new (no exact or close-enough match) is still left alone here, so
  // "+ Add ... as new" stays the only way to actually create one -- this
  // never auto-creates, only auto-resolves to something that already exists.
  function commitOnLeave() {
    const trimmed = (query ?? "").trim();
    if (!trimmed) {
      setQuery(null);
      setActive(-1);
      return;
    }
    const match = options.find((o) => o.label.toLowerCase() === trimmed.toLowerCase());
    if (match) {
      pick(match);
      return;
    }
    const fuzzy = closestFuzzyMatch(trimmed, options, (o) => o.label);
    if (fuzzy) {
      pick(fuzzy);
      return;
    }
    setQuery(null);
    setActive(-1);
  }

  useEffect(() => {
    if (!editing) return;
    function onDocMouseDown(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        commitOnLeave();
      }
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, query, options]);

  return (
    <div className="combobox" ref={boxRef} style={{ position: "relative" }}>
      <input
        value={editing ? query : selectedLabel}
        placeholder={placeholder}
        aria-label={ariaLabel}
        onFocus={() => { setQuery(""); setActive(-1); }}
        onChange={(e) => { setQuery(e.target.value); setActive(-1); }}
        onBlur={() => commitOnLeave()}
        onKeyDown={(e) => {
          if (!editing) return;
          if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, totalRows - 1)); }
          if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, -1)); }
          if (e.key === "Enter") {
            e.preventDefault();
            if (active >= 0 && active < filtered.length) pick(filtered[active]!);
            else if (showCreate && active === filtered.length) create();
            else if (filtered.length === 1 && !showCreate) pick(filtered[0]!);
          }
          if (e.key === "Escape") { setQuery(null); setActive(-1); }
        }}
      />
      {editing && (filtered.length > 0 || showCreate) && (
        <div className="suggest-list" role="listbox" aria-label={ariaLabel}>
          {filtered.map((o, i) => (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={i === active}
              className="suggest-item"
              data-active={i === active || undefined}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(o)}
            >
              {o.label}
            </button>
          ))}
          {showCreate && (
            <button
              type="button"
              className="suggest-item"
              data-active={active === filtered.length || undefined}
              onMouseDown={(e) => e.preventDefault()}
              onClick={create}
            >
              {createLabel ? createLabel(query.trim()) : `+ Add "${query.trim()}"`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * An inline "pick from a short suggested list, or type your own" chooser
 * — used for things like room/floor names, where a plain text box gives
 * no hint of the common answers (Glide showed a preset list here: room
 * types, "Ground"/"1st"/etc.). Selecting a preset or typing a custom name
 * both just arm the confirm button rather than submitting on tap/keypress,
 * so a stray click never adds the wrong name before you meant to commit.
 */
export function PresetPicker({
  presets,
  onSubmit,
  onCancel,
  placeholder = "Custom name…",
}: {
  presets: string[];
  onSubmit: (name: string) => void;
  onCancel: () => void;
  placeholder?: string;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const [custom, setCustom] = useState(false);
  const [customValue, setCustomValue] = useState("");

  const value = (custom ? customValue.trim() : selected) || "";

  function submit() {
    if (value) onSubmit(value);
  }

  return (
    <div className="preset-picker">
      <div className="suggest-panel">
        {presets.map((p) => (
          <button
            key={p}
            type="button"
            className="suggest-item"
            data-active={(!custom && selected === p) || undefined}
            onClick={() => { setSelected(p); setCustom(false); }}
          >
            <span>{p}</span>
            {!custom && selected === p && <span aria-hidden="true">✓</span>}
          </button>
        ))}
        <button
          type="button"
          className="suggest-item"
          data-active={custom || undefined}
          onClick={() => setCustom(true)}
        >
          <span>Custom…</span>
          {custom && <span aria-hidden="true">✓</span>}
        </button>
      </div>
      {custom && (
        <input
          autoFocus
          value={customValue}
          onChange={(e) => setCustomValue(e.target.value)}
          placeholder={placeholder}
          onKeyDown={(e) => {
            if (e.key === "Enter" && customValue.trim()) onSubmit(customValue.trim());
            if (e.key === "Escape") onCancel();
          }}
          style={{ marginTop: 6 }}
        />
      )}
      <div className="row-tight" style={{ justifyContent: "flex-end", marginTop: 6 }}>
        <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button size="sm" onClick={submit} disabled={!value}>✓ Add</Button>
      </div>
    </div>
  );
}

/**
 * A plain free-text input with a click-to-fill suggestions dropdown —
 * the mobile-safe replacement for `<input list="...">` + `<datalist>`,
 * which iOS Safari silently never renders. Unlike Combobox, there's no
 * separate value/label or a create step: whatever's typed IS the value,
 * committed on every keystroke exactly like a plain input; suggestions
 * are purely a convenience for filling the field in one tap, matching
 * datalist's actual behaviour rather than Combobox's pick-or-create one.
 */
export function SuggestField({
  value,
  onChange,
  suggestions,
  placeholder,
  ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  suggestions: string[];
  placeholder?: string;
  ariaLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const needle = value.trim().toLowerCase();
  const filtered = needle
    ? suggestions.filter((s) => s.toLowerCase().includes(needle) && s.toLowerCase() !== needle)
    : suggestions;

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  return (
    <div className="combobox" ref={boxRef} style={{ position: "relative" }}>
      <input
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel}
        onFocus={() => setOpen(true)}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
      />
      {open && filtered.length > 0 && (
        <div className="suggest-list" role="listbox" aria-label={ariaLabel}>
          {filtered.map((s) => (
            <button
              key={s}
              type="button"
              role="option"
              className="suggest-item"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onChange(s); setOpen(false); }}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Loading({ rows = 4 }: { rows?: number }) {
  return (
    <div style={{ display: "grid", gap: 8, padding: 14 }} aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton" style={{ width: `${100 - i * 7}%` }} />
      ))}
    </div>
  );
}

export function Alert({
  tone = "bad",
  children,
}: {
  tone?: "bad" | "warn" | "good";
  children: ReactNode;
}) {
  return (
    <div className={`alert ${tone}`} role={tone === "bad" ? "alert" : "status"}>
      {children}
    </div>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  if (!error) return null;
  const msg =
    error instanceof Error
      ? error.message
      : typeof error === "object" && error && "message" in error
        ? String((error as { message: unknown }).message)
        : String(error);
  return <Alert tone="bad">{msg}</Alert>;
}

/**
 * Modal dialog. Traps nothing clever — it closes on Escape and on backdrop
 * click, restores focus, and moves focus inside on open. Enough for a form.
 */
export function Dialog({
  title,
  onClose,
  children,
  footer,
  size = "md",
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** "lg" for a dialog holding a form rather than a question -- a list of
   *  a device's sockets needs the width the default 520px does not give. */
  size?: "md" | "lg";
}) {
  const ref = useRef<HTMLDivElement>(null);
  const restoreTo = useRef<Element | null>(null);
  // Callers routinely pass an inline `onClose` (a new function identity on
  // every render), and typing anywhere in the dialog's own form re-renders
  // it — a ref keeps Escape/cleanup on the latest callback without that
  // making the mount effect below depend on onClose and re-fire per
  // keystroke, which used to steal focus back to the dialog's first
  // focusable field while typing.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    restoreTo.current = document.activeElement;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    document.addEventListener("keydown", onKey);
    // Focus the first control for keyboard users — but never a date/time/file
    // input. On mobile, focusing those opens the native picker (a calendar
    // for a date) the instant the dialog mounts, hiding the dialog's own
    // content behind it ("I click Manage booking and get a calendar"). Skip
    // past them to the first safe control, else focus the dialog itself.
    const picker = new Set(["date", "time", "datetime-local", "month", "week", "file"]);
    const focusables = ref.current?.querySelectorAll<HTMLElement>(
      "input,select,textarea,button",
    );
    let target: HTMLElement | null = null;
    for (const el of focusables ?? []) {
      if (el instanceof HTMLInputElement && picker.has(el.type)) continue;
      target = el;
      break;
    }
    (target ?? ref.current)?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      (restoreTo.current as HTMLElement | null)?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`dialog${size === "lg" ? " dialog-lg" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={ref}
      >
        <header className="dialog-head">
          <h2>{title}</h2>
        </header>
        <div className="dialog-body">{children}</div>
        {footer && <footer className="dialog-foot">{footer}</footer>}
      </div>
    </div>
  );
}

export function Tabs({
  tabs,
  active,
  onChange,
}: {
  tabs: { id: string; label: string; badge?: number }[];
  active: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.id}
          role="tab"
          type="button"
          className="tab"
          aria-selected={active === t.id}
          onClick={() => onChange(t.id)}
        >
          {t.label}
          {t.badge ? ` (${t.badge})` : ""}
        </button>
      ))}
    </div>
  );
}
